// External Imports
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import React from 'react';
import {
  styled,
  IconBackArrow,
  IconClose,
  Inline,
  NavBarWrapper,
  NavBarSection,
} from '@lessonnine/design-system.lib';

// Internal Imports
import { discoverDeeplinkUrl } from '../../utils/discoverDeeplinkUrl.js';
import { getEdition } from '../../selectors/getEdition.js';
import { getIsArticlePage } from '../../selectors/getIsArticlePage.js';
import { getLearnLanguage } from '../../selectors/getLearnLanguage.js';
import { getLocale } from '../../selectors/getLocale.js';
import { breakpoints } from '../../config/css/breakpoints.mjs';
import { I18n } from '../I18n/I18n.jsx';
import { LANGUAGES } from '../../config/types/prop.js';
import { learnLanguageToTag } from '../../utils/tagToLearnLanguage.js';
import { ButtonLink } from '../Link/ButtonLink.jsx';
import { DsLink } from '../Link/DsLink.jsx';
import { ConditionalDisplay } from '../ConditionalDisplay/ConditionalDisplay.jsx';
import { Logo } from './Logo.jsx';
import { options } from '../../config/reactRedux.js';

const FixedHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;

  @media ${breakpoints.desktop.min} {
    position: relative;
  }
`;

// Component Definition
function InProductHeaderBase({ currentEdition, isArticlePage, learnLanguage, locale }) {
  const linkTo = isArticlePage
    ? `/${currentEdition}/magazine/tags/${learnLanguageToTag(currentEdition, learnLanguage)}`
    : discoverDeeplinkUrl(learnLanguage, locale);
  return (
    <>
      <FixedHeader>
        <NavBarWrapper
          hasMobileMenu={false}
          showLogo={false}
          leftAlignedItems={
            <NavBarSection>
              <ConditionalDisplay hideUpTo="tabletPortrait">
                <Logo currentEdition={currentEdition} />
              </ConditionalDisplay>
              {!isArticlePage && (
                <ConditionalDisplay hideAbove="tabletPortrait">
                  <DsLink to={linkTo} underlined={false} weight="strong">
                    <Inline alignItems="center" gap={8}>
                      <IconBackArrow />
                      <I18n field="in-app-back-button-mobile" defaultText="Explore" />
                    </Inline>
                  </DsLink>
                </ConditionalDisplay>
              )}
            </NavBarSection>
          }
          rightAlignedItems={
            <NavBarSection>
              <ConditionalDisplay hideUpTo="tabletPortrait">
                <DsLink to={linkTo}>
                  <I18n
                    field={isArticlePage ? 'in-app-article-close' : 'in-app-back-button-desktop'}
                    defaultText={isArticlePage ? 'Close' : 'Back to explore'}
                  />
                </DsLink>
              </ConditionalDisplay>
              <ConditionalDisplay hideAbove="tabletPortrait">
                {isArticlePage && (
                  <ButtonLink color="tertiary" icon={<IconClose />} title="Close" to={linkTo} />
                )}
              </ConditionalDisplay>
            </NavBarSection>
          }
        />
      </FixedHeader>
      {!isArticlePage && (
        <Inline align="center" padding={[20, 0, 0, 0]}>
          <ConditionalDisplay hideAbove="tabletPortrait">
            <Logo currentEdition={currentEdition} />
          </ConditionalDisplay>
        </Inline>
      )}
    </>
  );
}

InProductHeaderBase.propTypes = {
  currentEdition: LANGUAGES.isRequired,
  isArticlePage: PropTypes.bool.isRequired,
  learnLanguage: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  currentEdition: getEdition(state),
  isArticlePage: getIsArticlePage(state),
  learnLanguage: getLearnLanguage(state),
  locale: getLocale(state),
});

const withRedux = connect(mapStateToProps, undefined, undefined, options);
const InProductHeader = withRouter(withRedux(InProductHeaderBase));

// Module Exports
export { InProductHeader };
